import { Box, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormProvider, useFormContext } from '@/components/react-hook-form';
import { DisplayTable } from '@/components/tables/DisplayTable/DisplayTable';
import { TableRowSkeleton } from '@/components/tables/TableRowSkeleton/TableRowSkeleton';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useLiabilitiesStepData } from '@/modules/household/LinkHouseholdDataTrowser/LiabilitiesStep/hooks/useLiabilitiesStepData';
import { LiabilitiesStepFormShape } from '@/modules/household/LinkHouseholdDataTrowser/LiabilitiesStep/LiabilitiesStep.types';
import { LiabilitiesStepEditDrawer } from '@/modules/household/LinkHouseholdDataTrowser/LiabilitiesStep/LiabilitiesStepEditDrawer';
import { LiabilitiesStepRow } from '@/modules/household/LinkHouseholdDataTrowser/LiabilitiesStep/LiabilitiesStepRow';
import { useIssuerAndRecipientOptionsGrouped } from '@/modules/liabilities/LiabilityModal/hooks/useRecipientOptions';
import { COLORS } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimalsAccounting } from '@/utils/formatting/currency';

import { useManageIntegrationDataContext } from '../../context/manageIntegrationData.context';
import { LiabilitiesImportForm } from './hooks/useLiabilitiesImportForm';

const COLUMNS = [
  { headerName: '', width: 40 },
  { headerName: 'Name', flex: 1 },
  { headerName: 'Lender', flex: 1 },
  { headerName: 'Debtor', flex: 1 },
  { headerName: 'Outstanding balance', width: 160, align: 'right' as const },
  { headerName: 'Other linked fields', width: 130 },
  { headerName: '', width: 48 },
];

export interface LiabilitiesImportPaneProps {
  form: LiabilitiesImportForm;
}

/** "Import data" → Liabilities (eMoney only). */
export function LiabilitiesImportPane({ form }: LiabilitiesImportPaneProps) {
  const { householdId } = useManageIntegrationDataContext();
  const { linkedHouseholdAssetProvider } = useHouseholdDetailsContext();
  const { formMethods, seed, setEntities } = form;

  const { defaultValues, integrationLiabilities, loading } =
    useLiabilitiesStepData({
      householdId,
      linkedHouseholdAssetProvider,
      // The pane only mounts once it's open, so this refires each time the user
      // returns — picking up a changed client selection, and dropping rows that
      // have since been imported.
      fetchPolicy: 'cache-and-network',
    });

  const sourceNames = useMemo(
    () =>
      Object.fromEntries(
        integrationLiabilities.map((liability) => [
          liability.id,
          liability.name,
        ])
      ),
    [integrationLiabilities]
  );
  const integrationRecipientNames = useMemo(
    () =>
      Object.fromEntries(
        integrationLiabilities.map((liability) => [
          liability.id,
          liability.recipientName,
        ])
      ),
    [integrationLiabilities]
  );

  useEffect(() => {
    if (loading) return;
    seed(defaultValues);
  }, [defaultValues, loading, seed]);

  return (
    <FormProvider<LiabilitiesStepFormShape> {...formMethods}>
      <LiabilitiesImportPaneInner
        loading={loading}
        sourceNames={sourceNames}
        integrationRecipientNames={integrationRecipientNames}
        setEntities={setEntities}
      />
    </FormProvider>
  );
}

function LiabilitiesImportPaneInner({
  loading,
  sourceNames,
  integrationRecipientNames,
  setEntities,
}: {
  loading: boolean;
  sourceNames: Record<string, string>;
  integrationRecipientNames: Record<string, string | null | undefined>;
  setEntities: LiabilitiesImportForm['setEntities'];
}) {
  const { isSaving } = useManageIntegrationDataContext();
  const { control } = useFormContext<LiabilitiesStepFormShape>();

  const [editingLiabilityId, setEditingLiabilityId] = useState<string | null>(
    null
  );

  const liabilities = useWatch<LiabilitiesStepFormShape>({
    control,
    name: 'liabilities',
  }) as LiabilitiesStepFormShape['liabilities'];

  const { options: issuerAndRecipientOptions, entities } =
    useIssuerAndRecipientOptionsGrouped({});

  useEffect(() => {
    setEntities(entities);
  }, [entities, setEntities]);

  const liabilityEntries = useMemo(
    () => Object.entries(liabilities ?? {}),
    [liabilities]
  );

  if (!loading && liabilityEntries.length === 0) {
    return (
      <Box py={4} textAlign="center">
        <Typography variant="body-s" color={COLORS.TEXT.SECONDARY}>
          No unlinked liabilities found for this household.
        </Typography>
      </Box>
    );
  }

  return (
    <FormFieldsDisabledProvider isDisabled={isSaving}>
      <DisplayTable columns={COLUMNS}>
        {loading
          ? Array.from({ length: 4 }, (_, index) => (
              <TableRowSkeleton key={index} columns={COLUMNS.length} />
            ))
          : liabilityEntries.map(([id, row]) => (
              <LiabilitiesStepRow
                key={id}
                liabilityId={id}
                name={row.name}
                outstandingBalance={
                  row.outstandingBalance
                    ? formatCurrencyNoDecimalsAccounting(row.outstandingBalance)
                    : null
                }
                issuerAndRecipientOptions={issuerAndRecipientOptions}
                onEdit={setEditingLiabilityId}
                importTableLayout
                sourceName={sourceNames[id] ?? row.name}
                integrationRecipientName={integrationRecipientNames[id]}
                disableUntilSelected
              />
            ))}
      </DisplayTable>

      {editingLiabilityId && (
        <LiabilitiesStepEditDrawer
          integrationLiabilityId={editingLiabilityId}
          onClose={() => setEditingLiabilityId(null)}
        />
      )}
    </FormFieldsDisabledProvider>
  );
}
